'use client';

import { getFormFieldKeyRegistry } from '@/components/Form/helper/formFieldKeys';
import type { FormContentItem, FormValues, Validator } from '@/components/Form/types';
import { type BankService } from '@/hooks/useBankService';
import type { InitGetCitiesResult } from '@/hooks/usePostcodeService';
import type { FormStoryblok } from '@/types/component-types-sb';
import * as Yup from 'yup';

import { evaluateCondition, getNestedConditions } from './conditionsHelper';
import { createInitialFieldSchema } from './schemaBuilders';
import { applyNumericMaxLength, applyRequiredValidation, applyValidator } from './validatorApplicators';

export type Services = {
  bankService: BankService;
  postCode: InitGetCitiesResult;
};

export const buildFieldSchema = (
  form: FormStoryblok,
  item: FormContentItem,
  formValues: FormValues,
  services: Services
) => {
  let fieldSchema = createInitialFieldSchema(item, formValues);

  let isRequired = false;
  let isNumeric = false;
  const hasMaxLength: boolean | number = false;
  let requiredErrorMessage: string = '';
  let numericErrorMessage: string = '';
  const maxLengthErrorMessage: string = '';
  const otherValidators: Validator[] = [];

  if (item.validators && Array.isArray(item.validators)) {
    item.validators.forEach((validator: Validator) => {
      if (validator.component === 'Numeric') {
        isNumeric = true;
        numericErrorMessage = validator.errorMessage || 'Bitte geben sie eine gültige Zahl ein.';
      }
      if (validator.component === 'Required') {
        isRequired = true;
        requiredErrorMessage = validator.errorMessage || 'Dieses Feld ist Pflicht.';
      } else {
        otherValidators.push(validator);
      }
    });
  }

  // Apply all validators
  otherValidators.forEach((validator: Validator) => {
    fieldSchema = applyValidator(validator, fieldSchema, formValues, services, numericErrorMessage);
  });

  // Apply numeric max length constraint if needed
  if (hasMaxLength && isNumeric) {
    fieldSchema = applyNumericMaxLength(fieldSchema, hasMaxLength, numericErrorMessage, maxLengthErrorMessage);
  }

  // Apply required validation based on conditions or isRequired flag
  fieldSchema = applyRequiredValidation(fieldSchema, item, form, isRequired, requiredErrorMessage, formValues);

  return fieldSchema;
};

export const processFormContent = (
  items: FormContentItem[],
  schemaDefinition: { [key: string]: Yup.Schema<unknown> },
  form: FormStoryblok,
  formValues: FormValues,
  services: Services,
  path: string
): void => {
  items.forEach((item) => {
    if (['FormPage', 'FormSection', 'FormFieldset'].includes(item.component)) {
      if (item.fields?.length) {
        item.fields.forEach((field: FormContentItem) =>
          processFormContent([field], schemaDefinition, form, formValues, services, `${path}/${field.id || field._uid}`)
        );
      }

      if (item.content?.length) {
        item.content.forEach((contentItem: FormContentItem) =>
          processFormContent([contentItem], schemaDefinition, form, formValues, services, `${path}/${contentItem.id}`)
        );
      }
    } else {
      const id = item.component === 'FormMarketingOptIn' ? 'marketing_opt_in' : item.id;
      if (!id) return;

      const visibilityConditions = getNestedConditions(item, form).filter(({ targetAction }) =>
        ['show', 'hide'].includes(targetAction || '')
      );
      const isVisible = visibilityConditions.every((condition) =>
        condition.targetAction === 'show'
          ? evaluateCondition(condition, formValues)
          : !evaluateCondition(condition, formValues)
      );

      // Validation is rebuilt from the current Formik values. Hidden branches
      // must not contribute validators, even when their IDs are unique.
      if (!isVisible) return;

      const stateKey = getFormFieldKeyRegistry(form).getStateKey(item);
      schemaDefinition[stateKey] = buildFieldSchema(form, item, formValues, services);
    }
  });
};

export const buildValidationSchema = (
  form: FormStoryblok,
  content: FormContentItem[] | undefined,
  formValues: FormValues,
  services: Services
) => {
  if (!content) {
    return undefined;
  }

  const schemaDefinition: { [key: string]: Yup.Schema<unknown> } = {};
  processFormContent(content, schemaDefinition, form, formValues, services, 'root');

  const schema = Yup.object().shape(schemaDefinition);
  return schema;
};

export const initializeFormValues = (
  form: FormStoryblok,
  urlParams: { [key: string]: string } = {}
): { [key: string]: unknown } => {
  const registry = getFormFieldKeyRegistry(form);
  const initialValues: FormValues = {};

  for (const [key, value] of Object.entries(urlParams)) {
    const stateKeys = registry.getStateKeysForValueId(key);
    if (stateKeys.length > 0) {
      stateKeys.forEach((stateKey) => {
        initialValues[stateKey] = value;
      });
    } else {
      initialValues[key] = value;
    }
  }

  const content = form.content;
  if (!content) {
    return initialValues;
  }
  const addInitialValues = (items: FormContentItem[]) => {
    for (const item of items) {
      if (['FormPage', 'FormSection', 'FormFieldset'].includes(item.component)) {
        addInitialValues(item.content || item.fields);
      } else {
        let initialValue;
        switch (item.component) {
          case 'FormCheckbox':
            initialValue = item.initialValue ? item.initialValue.split(',') : [];
            break;
          case 'FormMarketingOptIn':
            initialValue = [];
            break;
          case 'FormRadioGroup':
            initialValue = item.initialValue || '';
            break;
          case 'FormFileUpload':
            initialValue = item.initialValue || null;
            break;
          case 'FormDate':
            initialValue = item.initialValue || '';
            break;
          case 'FormSelect':
            if (item.initialValue) {
              initialValue = item.initialValue;
            } else if (item.placeholder) {
              initialValue = item.initialValue || '';
              // Do not select first value
              // } else if (item.options && item.options.length > 0) {
              //  initialValue = item.options[0].value;
            } else {
              initialValue = '';
            }
            break;
          case 'Autocomplete':
            break;
          default:
            initialValue = item.initialValue || '';
            break;
        }
        const stateKey = registry.getStateKey(item);
        if (stateKey && !(stateKey in initialValues)) {
          initialValues[stateKey] = initialValue;
        }
      }
    }
  };
  addInitialValues(content);
  return initialValues;
};
